import React, { useRef, useReducer } from 'react';
import PropTypes from 'prop-types';
import { resolutions, internalResolutions, filteredResolutions } from './RecorderConstants';
import useClickOutside from './hooks/useClickOutside';

/**
 * Component for rendering recorder options such as resolutions, camera devices, audio devices, and backgrounds
 */
const RecorderOptions = ({
    status,
    streamType,
    recordingTime,
    recordingLimit,
    audioDevices,
    cameraDevices,
    selectedResolution,
    backgrounds,
    portalHasVirtualBackgrounds,
    isBgEnabled,
    handleBlur = () => { },
    handleImage = () => { },
    setStream = () => { },
    setResolution = () => { },
    setInternalResolution = () => { },
    setBackgrounds = () => { },
    resetRecorder = () => { },
}) => {
    const dropdownRef = useRef(); // Reference to the dropdown element
    const [localState, setLocalState] = useReducer((prevState, newState) => ({ ...prevState, ...newState }), {
        showResolutions: false,
        showInternalResolutions: false,
        showVideoDevices: false,
        showAudioDevices: false,
        showVirtualBg: false,
        canShowInternalResolutions: backgrounds.filter((bg) => bg.selected).length > 0,
        selectedCameraId: null,
        selectedAudioId: null,
    });

    const {
        showResolutions, showInternalResolutions, showVideoDevices, showAudioDevices, showVirtualBg, canShowInternalResolutions, selectedCameraId, selectedAudioId,
    } = localState;

    // Custom hook to handle clicks outside the dropdown
    useClickOutside(dropdownRef, () => {
        // This function will be called when a click occurs outside the component
        closeDropdown(); // Implement your closeDropdown function here
    });

    // Function to toggle dropdown based on type
    const dropdownToggle = (type) => {
        if (type === 'showResolutions') {
            setLocalState({
                showVideoDevices: false,
                showAudioDevices: false,
                showResolutions: !showResolutions,
                showInternalResolutions: false,
                showVirtualBg: false,
            });
            return;
        }
        if (type === 'showInternalResolutions') {
            setLocalState({
                showVideoDevices: false,
                showAudioDevices: false,
                showResolutions: false,
                showInternalResolutions: !showInternalResolutions,
                showVirtualBg: false,
            });
            return;
        }
        if (type === 'showVideoDevices') {
            setLocalState({
                showVideoDevices: !showVideoDevices,
                showAudioDevices: false,
                showResolutions: false,
                showInternalResolutions: false,
                showVirtualBg: false,
            });
            return;
        }
        if (type === 'showAudioDevices') {
            setLocalState({
                showVideoDevices: false,
                showAudioDevices: !showAudioDevices,
                showResolutions: false,
                showInternalResolutions: false,
                showVirtualBg: false,
            });
            return;
        }
        if (type === 'showVirtualBg') {
            setLocalState({
                showVideoDevices: false,
                showAudioDevices: false,
                showResolutions: false,
                showInternalResolutions: false,
                showVirtualBg: !showVirtualBg,
            });
        }
    };

    // Function to close all dropdowns
    const closeDropdown = () => {
        setLocalState({
            showVideoDevices: false,
            showAudioDevices: false,
            showResolutions: false,
            showInternalResolutions: false,
            showVirtualBg: false,
        });
    };

    // Function to set camera device
    const setCamera = (device) => {
        // set which device is selected
        cameraDevices && cameraDevices.map((option) => {
            option.selected = false;
            return option;
        });
        device.selected = true;
        setLocalState({
            selectedCameraId: device.deviceId,
        });
        resetRecorder();
        setStream(selectedResolution, device.deviceId, selectedAudioId);
    };

    /*
     * Set the audio option for the recorder
     * @param {Object} device : device to change the audio
     */
    const setAudio = (device) => {
        // set which device is selected
        audioDevices && audioDevices.map((option) => {
            option.selected = false;
            return option;
        });
        device.selected = true;
        setLocalState({
            selectedAudioId: device.deviceId,
        });
        resetRecorder();
        setStream(selectedResolution, selectedCameraId, device.deviceId);
    };

    // Get the image input element by its id
    const imageInput = document.getElementById('imageInput');

    // Function to handle different background types and trigger appropriate actions
    const setBackgroundSelection = (background) => {
        if (background.type === 'virtual') {
            // For the 'virtual' background, trigger the click event on the file input element
            imageInput.click();
        } else if (background.type === 'blur') {
            handleBlur(11);
        } else {
            resolutions.forEach((res) => {
                if (res.value === selectedResolution) {
                    res.selected = true;
                } else {
                    res.selected = false;
                }
            });
            handleBlur(0);
        }
        // Set 'selected' property based on the selected background
        backgrounds.forEach((bg) => bg.selected = bg.type === background.type);
        const clearBackground = { label: 'Clear Background', type: 'clear', selected: false };
        if (backgrounds.filter((data) => data.type === 'clear').length === 0) {
            backgrounds.push(clearBackground);
        }
        if (backgrounds.filter((data) => data.type === 'clear' && data.selected).length === 1) {
            backgrounds.pop();
        }
        setLocalState({
            canShowInternalResolutions: backgrounds.filter((bg) => bg.selected).length > 0,
        });
        setBackgrounds(backgrounds);
    };

    // Function to handle image change event
    const handleImageChange = (event) => {
        const fileInput = event.target;
        const file = fileInput.files[0];
        if (file) {
            // Clear the input's value
            fileInput.value = '';
            const reader = new FileReader();
            reader.onload = function (e) {
                // Use the provided setImage function to update the image in your React state or component logic
                handleImage(e.target.result);
            };
            reader.readAsDataURL(file);
        }
    };

    // Render the top bar of the recorder interface
    return (
        <div className="tv-recorder__top-bar" ref={dropdownRef}>
            {/* Render recording timer if the status is 'recording' */}
            {
                (status === 'recording') && <div className="tv-recorder__timer">{recordingTime} / {recordingLimit}</div>
            }
            {/* Render options for configuring recording if the status is 'beginRecording' */}
            {
                status === 'beginRecording' && (
                    <>
                        {/* Render internal resolutions dropdown if virtual backgrounds are available and can be shown */}
                        {
                            portalHasVirtualBackgrounds && canShowInternalResolutions && (
                                <div className="dropdown">
                                    <button type="button" className="border-none btn btn--circle" onClick={() => dropdownToggle('showInternalResolutions')}>
                                        <span><i className="fas fa-cogs"></i></span>
                                    </button>

                                    <ul className="dropdown__menu dropdown__menu--selectable right-0" role="menu" style={{
                                        display: showInternalResolutions ? 'block' : 'none',
                                    }}>
                                        {
                                            internalResolutions && internalResolutions.map((resolution, index) => (
                                                <li className={`dropdown__menu-item ${resolution.selected ? 'selected' : ''}`} onClick={() =>
                                                    setInternalResolution(resolution)
                                                } key={`res-${index}`}>
                                                    {resolution.label}
                                                </li>
                                            ))
                                        }
                                    </ul>
                                </div>
                            )
                        }
                        {/* Render virtual backgrounds dropdown if virtual backgrounds are available */}
                        {
                            portalHasVirtualBackgrounds && (
                                <div className="dropdown">
                                    <button type="button" className="border-none btn btn--circle" onClick={() => dropdownToggle('showVirtualBg')}>
                                        <span><i className="fas fa-magic"></i></span>
                                    </button>
                                    {/* Add the file input element for image selection */}
                                    <input type="file" id="imageInput" accept="image/*" style={{
                                        display: 'none',
                                    }} onChange={handleImageChange}></input>

                                    <ul className="dropdown__menu dropdown__menu--selectable right-0" role="menu" style={{
                                        display: showVirtualBg ? 'block' : 'none',
                                    }}>
                                        {
                                            backgrounds && backgrounds.map((background, index) => (
                                                <li
                                                    className={`dropdown__menu-item ${background.selected ? 'selected' : ''}`}
                                                    onClick={() => setBackgroundSelection(background)}
                                                    key={`bg-${index}`}
                                                >
                                                    {background.label}
                                                </li>

                                            ))
                                        }
                                    </ul>
                                </div>
                            )
                        }
                        {/* Render resolutions dropdown */}
                        <div className="dropdown">
                            <button type="button" className="border-none btn btn--circle" onClick={() => dropdownToggle('showResolutions')}>
                                <span><i className="fas fa-cog"></i></span>
                            </button>
                            <ul className="dropdown__menu dropdown__menu--selectable right-0" role="menu" style={{
                                display: showResolutions ? 'block' : 'none',
                            }}>
                                {
                                    resolutions && (isBgEnabled ? filteredResolutions : resolutions).map((resolution, index) => (
                                        resolution.supported && (
                                            <li
                                                className={`dropdown__menu-item ${resolution.selected ? 'selected' : ''}`}
                                                onClick={() => setResolution(resolution)}
                                                key={`res-${index}`}
                                                disabled
                                            >
                                                {resolution.label}
                                            </li>
                                        )
                                    ))
                                }
                            </ul>
                        </div>
                        {/* Render video devices dropdown */}
                        <div className="dropdown">
                            <button type="button" className="border-none btn btn--circle" onClick={() => dropdownToggle('showVideoDevices')}>
                                <span><i className="fas fa-video"></i></span>
                            </button>
                            <ul className="dropdown__menu dropdown__menu--selectable right-0" role="menu" style={{
                                display: showVideoDevices ? 'block' : 'none',
                            }}>
                                {
                                    cameraDevices && cameraDevices.map((videoOption, index) => (
                                        <li className={`dropdown__menu-item ${videoOption.selected ? 'selected' : ''}`} onClick={() => setCamera(videoOption)} key={`vid-${index}`}>
                                            {videoOption.label}
                                        </li>
                                    ))
                                }
                            </ul>
                        </div>
                        {/* Render audio devices dropdown */}
                        <div className="dropdown">
                            <button type="button" className="border-none btn btn--circle" onClick={() => dropdownToggle('showAudioDevices')}>
                                <span><i className="fas fa-microphone"></i></span>
                            </button>
                            <ul className="dropdown__menu dropdown__menu--selectable right-0" role="menu" style={{
                                display: showAudioDevices ? 'block' : 'none',
                            }}>
                                {
                                    audioDevices && audioDevices.map((audioOption, index) => (
                                        <li className={`dropdown__menu-item ${audioOption.selected ? 'selected' : ''}`} onClick={() => setAudio(audioOption)} key={`aud-${index}`}>
                                            {audioOption.label}
                                        </li>
                                    ))
                                }
                            </ul>
                        </div>
                    </>
                )
            }
        </div>
    );
};

RecorderOptions.propTypes = {
    status: PropTypes.string,
    streamType: PropTypes.string,
    recordingTime: PropTypes.string,
    recordingLimit: PropTypes.string,
    selectedResolution: PropTypes.object,
    audioDevices: PropTypes.array,
    cameraDevices: PropTypes.array,
    backgrounds: PropTypes.array,
    portalHasVirtualBackgrounds: PropTypes.bool,
    isBgEnabled: PropTypes.bool,
    handleBlur: PropTypes.func,
    handleImage: PropTypes.func,
    setStream: PropTypes.func,
    setResolution: PropTypes.func,
    setInternalResolution: PropTypes.func,
    resetRecorder: PropTypes.func,
    setBackgrounds: PropTypes.func,
};

export default RecorderOptions;
